import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { RowSelectionState } from '@tanstack/react-table';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Search, X } from 'lucide-react';
import ActionsTable from './ActionsTable';
import ActionsKanban from './ActionsKanban';
import ActionsTimeline from './ActionsTimeline';
import { IapScope, INCIDENT, type ActionAssigneeKind } from './ICSConstants';
import { useViewState, type ViewType } from './hooks/useViewState';
import { useIncidentActions } from './hooks/incidentActionHooks';
import { useOrgActions } from './hooks/orgActionHooks';
import { useActionFieldEdit } from './hooks/useActionFieldEdit';
import { useActionAssigneeDirectory } from './hooks/useActionAssigneeDirectory';
import { useActionShortcuts } from './hooks/useActionShortcuts';
import {
  ACTION_GROUPING_LABELS,
  ACTION_GROUPINGS,
  type ActionGrouping,
} from './actionsTable/actionGrouping';
import {
  TIMELINE_RANGE_LABELS,
  TIMELINE_RANGES,
  type TimelineRange,
} from './actionsTable/actionTimeline';
import { ActionCommandPalette } from './actionsTable/ActionCommandPalette';
import { ShiftHandoverDialog } from './actionsTable/ShiftHandoverDialog';
import {
  actionsForHandover,
  describeBulkResult,
  handoverPositions,
  patchFromPalette,
  userAssignmentPatch,
  type BulkFailure,
  type BulkResult,
} from './actionsTable/actionBulkModel';
import { fromDateTimeLocalInput } from './actionsTable/actionEditModel';
import type { ActionPaletteCommand } from './actionsTable/actionPaletteModel';
import type { ActionEntity } from './hooks/useScopedActionForm';
import { useActualIncidentId } from './hooks/commonHooks';
import MyAssignedActionsSummary from './MyAssignedActionsSummary';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import { ActionsViewMenu } from './actionsTable/ActionsViewMenu';
import { ActionsFilterMenu } from './actionsTable/ActionsFilterMenu';
import ActionAssigneeFilter, {
  ALL_ASSIGNEES,
  matchesAssigneeFilter,
} from './actionsTable/ActionAssigneeFilter';
import { useActionColumnVisibility } from './actionsTable/useActionColumnVisibility';
import {
  EMPTY_ACTION_LIST_FILTERS,
  buildActionFilterChips,
  buildActionFilterColumns,
  collectMineActionIds,
  dismissActionListFilterChip,
  filterActions,
  hasActionNarrowingFilters,
  type ActionListFilters,
} from './actionsTable/actionListFilters';
import { useMyTasks } from 'components/MyTasks/useMyTasks';
import { selectUserGuid, useAppSelector } from 'slices/commonSelectors';
import { useHwcgFlag } from 'utils/featureFlags';
import { useAorText } from 'utils/aorTerminology';
import { registerHwcgTourApi } from 'components/IncidentConfig/hwcgTour/hwcgTourBridge';
import {
  countLocatedActions,
  describeAreaFilter,
} from './utils/actionSpatialFilter';

type ActionsWorkspaceProps = {
  scope?: IapScope;
  incidentIdOverride?: string | null;
  canEdit?: boolean;
  onEditAction: (action: ActionEntity) => void;
  toolbar?: React.ReactNode;
  storageKey?: string;
};

function isGrouping(value: string): value is ActionGrouping {
  return (ACTION_GROUPINGS as readonly string[]).includes(value);
}

const HWCG_ASSIGNEE_KINDS: readonly ActionAssigneeKind[] = ['user', 'position'];

function isRange(value: string): value is TimelineRange {
  return (TIMELINE_RANGES as readonly string[]).includes(value);
}

/**
 * The three ways of looking at the same work, plus the controls that switch
 * between them. Corporate View and the incident workspace both render this so a
 * view preference is the same place either way.
 */
const ActionsWorkspace: React.FC<ActionsWorkspaceProps> = ({
  scope = INCIDENT,
  incidentIdOverride,
  canEdit = true,
  onEditAction,
  toolbar,
  storageKey = 'actions-view-preference',
}) => {
  const { activeView, setActiveView } = useViewState({ storageKey });
  const [grouping, setGrouping] = useState<ActionGrouping>('status');
  const [range, setRange] = useState<TimelineRange>('week');
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [listFilters, setListFilters] = useState<ActionListFilters>(
    EMPTY_ACTION_LIST_FILTERS
  );
  const [assigneeFilter, setAssigneeFilter] = useState(ALL_ASSIGNEES);
  const [showRosterPositions, setShowRosterPositions] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [handoverOpen, setHandoverOpen] = useState(false);
  const [dueOpen, setDueOpen] = useState(false);
  const [dueDraft, setDueDraft] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);

  const isIncident = scope === INCIDENT;
  const routeIncidentId = useActualIncidentId();
  const incidentId = incidentIdOverride ?? routeIncidentId;
  const incidentQuery = useIncidentActions(scope, incidentIdOverride);
  const orgQuery = useOrgActions(scope);
  const actions = isIncident ? incidentQuery.data : orgQuery.data;
  const { editAction } = useActionFieldEdit(scope, incidentIdOverride);
  const directory = useActionAssigneeDirectory(scope, incidentIdOverride);
  const hwcg = useHwcgFlag();
  const aorText = useAorText();
  const userGuid = useAppSelector(selectUserGuid);
  const { data: myTasks } = useMyTasks();
  const { isMapVisible, actionAreaFilter, setActionAreaFilter } =
    useUscgMapContext();
  const { hiddenColumns, columnVisibility, onToggle, onReset } =
    useActionColumnVisibility(scope, isMapVisible);

  const activeActions = useMemo(
    () => (actions ?? []).filter((action) => !action.archived),
    [actions]
  );
  const userPositionNames = useMemo(
    () =>
      (directory.roster ?? [])
        .find((member) => member.userId === userGuid)
        ?.incidentPositions.map((position) => position.positionName) ?? [],
    [directory.roster, userGuid]
  );
  const timelineLanePositions = useMemo(
    () =>
      directory.positions.map((position) => ({
        id: position.value,
        name: position.label,
      })),
    [directory.positions]
  );
  const showPositionLanes = hwcg && isIncident && showRosterPositions;
  const filterColumns = useMemo(
    () => buildActionFilterColumns(activeActions, scope, directory, aorText),
    [activeActions, directory, scope, aorText]
  );
  const mineActionIds = useMemo(
    () =>
      collectMineActionIds(
        (myTasks?.tasks ?? []).map((task) => task.action_id),
        activeActions,
        userGuid
      ),
    [activeActions, myTasks?.tasks, userGuid]
  );
  const filteredActions = useMemo(() => {
    const listed = filterActions(
      activeActions,
      listFilters,
      directory,
      actionAreaFilter,
      mineActionIds,
      userPositionNames
    );
    if (!hwcg || assigneeFilter === ALL_ASSIGNEES) return listed;
    return listed.filter((action) =>
      matchesAssigneeFilter(action, directory, assigneeFilter)
    );
  }, [
    actionAreaFilter,
    activeActions,
    assigneeFilter,
    directory,
    hwcg,
    listFilters,
    mineActionIds,
    userPositionNames,
  ]);
  const filtersActive = hasActionNarrowingFilters(
    listFilters,
    actionAreaFilter
  );
  const areaFilterSummary = useMemo(() => {
    if (!actionAreaFilter) return undefined;
    return describeAreaFilter(
      activeActions.length,
      filteredActions.length,
      countLocatedActions(activeActions)
    );
  }, [actionAreaFilter, activeActions, filteredActions.length]);
  const filterChips = useMemo(
    () => buildActionFilterChips(listFilters, filterColumns, actionAreaFilter),
    [actionAreaFilter, filterColumns, listFilters]
  );

  const selectedActions = useMemo(() => {
    const ids = new Set(
      Object.entries(selectedRows)
        .filter(([, selected]) => selected)
        .map(([id]) => id)
    );
    return (actions ?? []).filter((action) => ids.has(String(action.id)));
  }, [actions, selectedRows]);

  const canHandover =
    canEdit && handoverPositions(actions ?? [], directory).length > 0;

  useActionShortcuts(selectedActions, canEdit && activeView === 'table');

  const activeActionsRef = useRef(activeActions);
  activeActionsRef.current = activeActions;
  const onEditActionRef = useRef(onEditAction);
  onEditActionRef.current = onEditAction;

  useEffect(
    () =>
      registerHwcgTourApi({
        setTasksView: setActiveView,
        openFirstTask: () => {
          const first = activeActionsRef.current[0];
          if (!first) return false;
          onEditActionRef.current(first);
          return true;
        },
      }),
    [setActiveView]
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const applyBulk = useCallback(
    async (
      targets: ActionEntity[],
      patch: Record<string, unknown>
    ): Promise<BulkResult> => {
      const failures: BulkFailure[] = [];
      let succeeded = 0;
      for (const action of targets) {
        const ok = await editAction(action, patch);
        if (ok) succeeded += 1;
        else {
          failures.push({
            actionId: String(action.action_id),
            name: action.name || 'Untitled task',
            message: 'Update failed',
          });
        }
      }
      return { attempted: targets.length, succeeded, failures };
    },
    [editAction]
  );

  const report = (result: BulkResult) => {
    const message = describeBulkResult(result);
    if (result.failures.length > 0) toast.error(message);
    else toast.success(message);
  };

  const runCommand = async (command: ActionPaletteCommand) => {
    if (command.kind === 'view' && command.view) {
      setActiveView(command.view as ViewType);
      return;
    }
    if (command.kind === 'handover') {
      setHandoverOpen(true);
      return;
    }
    if (command.kind === 'due') {
      if (selectedActions.length === 0) {
        toast.info('Select one or more tasks first.');
        return;
      }
      setDueOpen(true);
      return;
    }
    if (!command.patch) return;
    if (selectedActions.length === 0) {
      toast.info('Select one or more tasks first.');
      return;
    }
    setBulkBusy(true);
    const result = await applyBulk(
      selectedActions,
      patchFromPalette(command.patch)
    );
    setBulkBusy(false);
    report(result);
  };

  const handleHandover = async (positionId: string, userId: string) => {
    const targets = actionsForHandover(actions ?? [], positionId);
    setBulkBusy(true);
    const result = await applyBulk(targets, userAssignmentPatch(userId));
    setBulkBusy(false);
    setHandoverOpen(false);
    report(result);
  };

  const handleBulkDue = async () => {
    const next = fromDateTimeLocalInput(dueDraft);
    setBulkBusy(true);
    const result = await applyBulk(selectedActions, { target_date: next });
    setBulkBusy(false);
    setDueOpen(false);
    report(result);
  };

  return (
    <div className="dark tw:text-foreground">
      <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-2 tw:pb-2">
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
          {isIncident ? (
            <MyAssignedActionsSummary incidentId={incidentId} />
          ) : null}
          {activeView === 'kanban' ? (
            <div className="tw:flex tw:items-center tw:gap-2">
              <Label className="tw:text-xs tw:text-foreground">Group by</Label>
              <Select
                value={grouping}
                onValueChange={(next) => {
                  const value = String(next ?? '');
                  if (isGrouping(value)) setGrouping(value);
                }}
              >
                <SelectTrigger
                  size="sm"
                  aria-label="Group by"
                  className="tw:w-fit"
                >
                  <SelectValue>{ACTION_GROUPING_LABELS[grouping]}</SelectValue>
                </SelectTrigger>
                <SelectContent className="dark">
                  {ACTION_GROUPINGS.map((id) => (
                    <SelectItem key={id} value={id}>
                      {ACTION_GROUPING_LABELS[id]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          {activeView === 'timeline' ? (
            <div className="tw:flex tw:items-center tw:gap-2">
              <Label className="tw:text-xs tw:text-foreground">Window</Label>
              <Select
                value={range}
                onValueChange={(next) => {
                  const value = String(next ?? '');
                  if (isRange(value)) setRange(value);
                }}
              >
                <SelectTrigger
                  size="sm"
                  aria-label="Timeline window"
                  className="tw:w-fit"
                >
                  <SelectValue>{TIMELINE_RANGE_LABELS[range]}</SelectValue>
                </SelectTrigger>
                <SelectContent className="dark">
                  {TIMELINE_RANGES.map((id) => (
                    <SelectItem key={id} value={id}>
                      {TIMELINE_RANGE_LABELS[id]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {hwcg && isIncident ? (
                <Button
                  type="button"
                  variant={showRosterPositions ? 'default' : 'outline'}
                  size="sm"
                  aria-pressed={showRosterPositions}
                  onClick={() => setShowRosterPositions((current) => !current)}
                >
                  Show roster positions
                </Button>
              ) : null}
            </div>
          ) : null}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPaletteOpen(true)}
          >
            Bulk Update
            <kbd className="tw:rounded tw:border tw:border-border tw:bg-muted tw:px-1 tw:text-[10px]">
              ⌘K
            </kbd>
          </Button>
        </div>
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
          <div className="tw:relative tw:min-w-[12rem] tw:flex-1 tw:max-w-xs">
            <Search className="tw:pointer-events-none tw:absolute tw:top-1/2 tw:left-2 tw:size-3.5 tw:-translate-y-1/2 tw:text-muted-foreground" />
            <Input
              type="search"
              value={listFilters.search}
              onChange={(event) =>
                setListFilters((current) => ({
                  ...current,
                  search: event.target.value,
                }))
              }
              placeholder="Search tasks"
              aria-label="Search tasks"
              className="tw:h-8 tw:ps-8 tw:text-xs"
            />
          </div>
          {hwcg ? (
            <Button
              type="button"
              variant={listFilters.mine ? 'default' : 'outline'}
              size="sm"
              aria-pressed={listFilters.mine}
              onClick={() =>
                setListFilters((current) => ({
                  ...current,
                  mine: !current.mine,
                }))
              }
            >
              My Tasks
            </Button>
          ) : null}
          {hwcg ? (
            <Button
              type="button"
              variant={listFilters.needsApproval ? 'default' : 'outline'}
              size="sm"
              aria-pressed={listFilters.needsApproval}
              onClick={() =>
                setListFilters((current) => ({
                  ...current,
                  needsApproval: !current.needsApproval,
                }))
              }
            >
              Needs My Approval
            </Button>
          ) : null}
          {hwcg ? (
            <ActionAssigneeFilter
              directory={directory}
              value={assigneeFilter}
              onChange={setAssigneeFilter}
              kinds={HWCG_ASSIGNEE_KINDS}
            />
          ) : null}
          <ActionsFilterMenu
            filters={listFilters}
            onFiltersChange={setListFilters}
            columns={filterColumns}
            areaFilter={actionAreaFilter}
            onAreaFilterChange={setActionAreaFilter}
            areaSummary={areaFilterSummary}
            filtersActive={filtersActive}
            showMine={hwcg}
          />
          <ActionsViewMenu
            view={activeView}
            onViewChange={setActiveView}
            scope={scope}
            hiddenColumns={hiddenColumns}
            onToggleColumn={onToggle}
            onResetColumns={onReset}
          />
          {toolbar}
        </div>
      </div>

      {filterChips.length > 0 ? (
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-1.5 tw:pb-2">
          {filterChips.map((chip) => (
            <Badge
              key={chip.id}
              variant="outline"
              className="tw:h-6 tw:gap-1 tw:pr-1"
            >
              <span className="tw:max-w-[12rem] tw:truncate">{chip.label}</span>
              <button
                type="button"
                className="tw:inline-flex tw:rounded-sm tw:p-0.5 tw:text-muted-foreground tw:hover:bg-muted tw:hover:text-foreground"
                aria-label={`Remove ${chip.label} filter`}
                onClick={() => {
                  if (chip.id === 'area') {
                    setActionAreaFilter(null);
                    return;
                  }
                  setListFilters((current) =>
                    dismissActionListFilterChip(current, chip.id)
                  );
                }}
              >
                <X className="tw:size-3" />
              </button>
            </Badge>
          ))}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setListFilters(EMPTY_ACTION_LIST_FILTERS);
              setActionAreaFilter(null);
            }}
          >
            Clear all
          </Button>
        </div>
      ) : null}

      {selectedActions.length > 0 && canEdit ? (
        <div className="tw:mb-2 tw:flex tw:flex-wrap tw:items-center tw:gap-2">
          <span className="tw:text-xs tw:text-foreground">
            {selectedActions.length} selected
          </span>
          {canHandover ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setHandoverOpen(true)}
            >
              Shift handover
            </Button>
          ) : null}
        </div>
      ) : null}

      {activeView === 'table' ? (
        <ActionsTable
          scope={scope}
          incidentIdOverride={incidentIdOverride}
          onEditAction={onEditAction}
          canEdit={canEdit}
          selectedRows={selectedRows}
          onSelectedRowsChange={setSelectedRows}
          filteredActions={filteredActions}
          columnVisibility={columnVisibility}
          hasActiveFilters={filtersActive}
        />
      ) : null}
      {activeView === 'kanban' ? (
        <ActionsKanban
          scope={scope}
          incidentIdOverride={incidentIdOverride}
          grouping={grouping}
          onEditAction={onEditAction}
          canEdit={canEdit}
          filteredActions={filteredActions}
        />
      ) : null}
      {activeView === 'timeline' ? (
        <ActionsTimeline
          scope={scope}
          incidentIdOverride={incidentIdOverride}
          range={range}
          onEditAction={onEditAction}
          filteredActions={filteredActions}
          showRosterPositions={showPositionLanes}
          rosterPositions={timelineLanePositions}
          roster={directory.roster}
        />
      ) : null}

      <ActionCommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        directory={directory}
        canHandover={canHandover}
        onRun={(command) => {
          void runCommand(command);
        }}
      />
      <ShiftHandoverDialog
        show={handoverOpen}
        onClose={() => setHandoverOpen(false)}
        actions={actions ?? []}
        directory={directory}
        onConfirm={(positionId, userId) => {
          void handleHandover(positionId, userId);
        }}
        isSaving={bulkBusy}
      />
      <Dialog
        open={dueOpen}
        onOpenChange={(next) => {
          if (!next) setDueOpen(false);
        }}
      >
        <DialogContent className="dark">
          <DialogHeader>
            <DialogTitle>Set due date</DialogTitle>
          </DialogHeader>
          <Label htmlFor="bulk-due">Due</Label>
          <Input
            id="bulk-due"
            type="datetime-local"
            value={dueDraft}
            onChange={(event) => setDueDraft(event.target.value)}
          />
          <p className="tw:mb-0 tw:text-xs tw:text-foreground">
            Applies to {selectedActions.length} selected{' '}
            {selectedActions.length === 1 ? 'task' : 'tasks'}.
          </p>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDueOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={bulkBusy}
              onClick={() => {
                void handleBulkDue();
              }}
            >
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ActionsWorkspace;
export type { ActionsWorkspaceProps };
